import { useNavigate } from 'react-router-dom'

import { Box, Chevron, Tag, TagColor, Text, Touchable } from '@factorialco/deprecated-design-system'

import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { PayrollStatus } from 'modules/payroll_bundle/lib/enums'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import type PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'

interface Props {
  selectedPolicyId: string
  currentPeriod: PolicyPeriod | undefined
  periodId: string
}

const PayrollPeriodSelector = ({ selectedPolicyId, periodId, currentPeriod }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const {
    loadingPolicyPeriodCollection,
    setPeriodIndex,
    setCurrentPolicy,
    periodIndex,
    currentPolicyPeriods,
    setPeriodId,
  } = usePolicyPeriods()

  useEffect(() => {
    if (periodId !== currentPeriod?.attributes.id && periodId) {
      setPeriodId(periodId)
    }
  }, [periodId, currentPeriod?.attributes.id, setPeriodId])

  const statusColor = () => {
    switch (currentPeriod?.attributes.status) {
      case PayrollStatus.PAYROLL_CALCULATION:
        return TagColor.radical
      case PayrollStatus.PAID:
        return TagColor.lime
      default:
        return TagColor.malibu
    }
  }

  useUnsafeEffect(() => {
    if (loadingPolicyPeriodCollection) return
    setCurrentPolicy(selectedPolicyId)
  }, [selectedPolicyId, loadingPolicyPeriodCollection]) // eslint-disable-line react-hooks/exhaustive-deps

  const statusTranslation = () => {
    switch (currentPeriod?.attributes.status) {
      case PayrollStatus.PAYROLL_CALCULATION:
        return i18n.t('bookkeepers.clients.payroll_periods.payroll_calculation')
      case PayrollStatus.PAYMENT_PROCESSING:
      case PayrollStatus.PAID:
        return i18n.t('bookkeepers.clients.payroll_periods.payment')
      default:
        return i18n.t('bookkeepers.clients.payroll_periods.defining_supplements')
    }
  }

  const buildLink = (periodId: string, policyId: string) => {
    return navigation.bookkeepers.clients.payrollResults.payrollPolicy.payrollPolicyPeriodId.fullPath(
      {
        periodId,
        policyId,
      }
    )
  }

  const handlePrev = () => {
    setPeriodIndex(periodIndex - 1)
    const prevPeriod = currentPolicyPeriods[periodIndex - 1]

    if (!prevPeriod) return
    navigate(buildLink(prevPeriod?.attributes.id, selectedPolicyId))
  }

  const handleNext = () => {
    setPeriodIndex(periodIndex + 1)
    const nextPeriod = currentPolicyPeriods[periodIndex + 1]

    if (!nextPeriod) return
    navigate(buildLink(nextPeriod?.attributes.id, selectedPolicyId))
  }

  const hasPrev = periodIndex > 0
  const hasNext = periodIndex < currentPolicyPeriods.length - 1

  if (!currentPolicyPeriods.length) return <></>

  return (
    <Box
      border={{
        bottom: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      padding="s16"
      flexDirection="row"
      justifyContent="center"
      alignItems="center"
      gap="s32"
    >
      <Box width="s16">
        {hasPrev && (
          <Touchable onClick={handlePrev} aria-label="previous">
            <Chevron type="primary" direction="left" />
          </Touchable>
        )}
      </Box>
      <Box flexDirection="row" gap="s16">
        <Text.Normal color="grey1000" weight="medium">
          {currentPeriod?.get('name')}
        </Text.Normal>
        <Tag leftItem="circle" showCircle type={statusColor()}>
          {statusTranslation()}
        </Tag>
      </Box>
      <Box width="s16">
        {hasNext && (
          <Touchable onClick={handleNext} aria-label="next">
            <Chevron type="primary" direction="right" />
          </Touchable>
        )}
      </Box>
    </Box>
  )
}

export default PayrollPeriodSelector
